import { Analytics } from '@cobliteam/analytics';
import { useEffect, useState } from 'react';

export interface UseSuggestionResult {
  shouldShow: boolean;
  dismiss: (reason?: string) => void;
  accept: (metadata?: Record<string, unknown>) => void;
  trackClick: (metadata?: Record<string, unknown>) => void;
}

const readDismissed = (id: string): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    return window.sessionStorage.getItem(`suggestion:${id}:dismissed`) === 'true';
  } catch {
    return false;
  }
};

const persistDismissed = (id: string) => {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(`suggestion:${id}:dismissed`, 'true');
  } catch {
    // sessionStorage unavailable (private mode, quota exceeded) — fall back to
    // in-memory only; the suggestion will reappear on next mount.
  }
};

export const useSuggestion = (id: string, useShouldShow: () => boolean): UseSuggestionResult => {
  const [dismissed, setDismissed] = useState<boolean>(() => readDismissed(id));
  const shouldShow = useShouldShow() && !dismissed;

  // Re-sync `dismissed` whenever the suggestion id changes so a hook reused
  // with a different id does not leak the previous id's dismiss state.
  useEffect(() => {
    setDismissed(readDismissed(id));
  }, [id]);

  useEffect(() => {
    if (shouldShow) {
      Analytics.track('Suggestion Displayed', { suggestionId: id });
    }
  }, [shouldShow, id]);

  return {
    shouldShow,
    dismiss: (reason) => {
      Analytics.track('Suggestion Dismissed', { suggestionId: id, reason });
      persistDismissed(id);
      setDismissed(true);
    },
    // Spread metadata first so callers cannot accidentally overwrite the
    // suggestionId — it must always reflect the hook's own id.
    accept: (metadata) => {
      Analytics.track('Suggestion Accepted', { ...metadata, suggestionId: id });
    },
    trackClick: (metadata) => {
      Analytics.track('Suggestion Clicked', { ...metadata, suggestionId: id });
    },
  };
};

export default useSuggestion;
